import React from "react";
import {
  IOrdersFilters,
  IOrderRawWithMerchantName,
  IPackageToRefund,
  IUpdateOrderCsvResponseDTO,
  IOrdersFiltersV2,
  IOrderStatus,
  IShipmentUnit,
} from "../interfaces/order";
import {
  ABLE_TO_REFUND_OR_CANCEL,
  ECOMMERCE,
  LOCALE,
  MERCHANT,
  ORDER_STATUS,
  OWN_FLEET_ORDER_STATUSES,
  SP_DELIVERED,
  STATUS_TRANSLATOR,
} from "./constants";
import {
  TEntitiesAbleToExecuteAction,
  TEntity,
} from "../interfaces/userContext";
import { TCSVUploadStatus } from "../interfaces/configuration";

// Previous version left an extra space between day and
// hour (since it splitted by "," instead of ", ") and
// also did not work in firefox
const getDayAndTime = (date: string): string[] =>
  date.split(date.includes(",") ? ", " : " ");

export const mongoTimeStampToString = (
  date: string,
  trimTime?: boolean,
): string => {
  if (!date) return "";

  const asString = new Date(date).toLocaleString(LOCALE, { hour12: false });
  const [day, time] = getDayAndTime(asString);
  const [hour, minutes] = time.split(":");

  if (!trimTime) return `${day} ${hour}:${minutes}`;

  return day;
};

export const downloadFromURL = (url: string) => window.open(url, "_blank");

export const getOrderStatus = (idToFind: string, service?: string) =>
  ORDER_STATUS.find((s) => s.id === idToFind && (!s.service || s.service === service));

export const getOrderStatuses = (
  ids: string[] = [],
  options?: { ownFleet?: boolean },
): IOrderStatus[] => {
  if (!Array.isArray(ids) || ids.length === 0) return [];
  const source = options?.ownFleet ? OWN_FLEET_ORDER_STATUSES : ORDER_STATUS;
  const idSet = new Set(ids.map((s) => String(s).toLowerCase()));
  return source.filter((st) => idSet.has(String(st.id).toLowerCase()));
};

export const translateStatus = (status: string) =>
  getOrderStatus(status.toLowerCase());

export const getFiltersQuery = (filters: IOrdersFilters) => {
  let query = "";

  Object.keys(filters).forEach((keyName) => {
    if (filters[keyName]) query = `${query}&${keyName}=${filters[keyName]}`;
  });
  return query;
};

const isDefined = (v: unknown): v is string | number =>
  v !== undefined && v !== null;
const hasItems = (arr?: unknown[]): arr is Array<string | number> =>
  Array.isArray(arr) && arr.length > 0;

export const buildOrdersV2Query = (
  entity: string,
  marketplaceId?: string,
  filtros?: IOrdersFiltersV2,
): string => {
  let query = `${
    entity === MERCHANT
      ? `?marketplaceId=${encodeURIComponent(String(marketplaceId))}`
      : "?"
  }`;
  if (!filtros) return query;

  const appendScalar = (key: string, value: string | number) => {
    query += `&${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`;
  };
  const appendArray = (key: string, values: Array<string | number>) => {
    values.forEach((v) => {
      query += `&${encodeURIComponent(key)}[]=${encodeURIComponent(String(v))}`;
    });
  };

  const {
    skip,
    limit,
    sort,
    search,
    date_from,
    date_to,
    carrier,
    _id,
    status,
    tracking_id,
    clientId,
    merchantId,
    sla_alert_level,
  } = filtros;

  const normalizedSearch =
    isDefined(search) && String(search).length > 0 ? search : undefined;

  const scalarParams: Array<[string, string | number | undefined | null]> = [
    ["skip", skip],
    ["limit", limit],
    ["clientId", clientId],
    ["merchantId", merchantId],
    ["sort", sort],
    ["search", normalizedSearch],
    ["date_from", date_from],
    ["date_to", date_to],
  ];

  scalarParams.forEach(([key, value]) => {
    if (isDefined(value)) appendScalar(key, value as string | number);
  });

  const arrayParams: Array<[string, Array<string | number> | undefined]> = [
    ["carrier", carrier as unknown as Array<string | number> | undefined],
    ["_id", _id as unknown as Array<string | number> | undefined],
    ["status", status as unknown as Array<string | number> | undefined],
    [
      "tracking_id",
      tracking_id as unknown as Array<string | number> | undefined,
    ],
    ["sla_alert_level", sla_alert_level as unknown as Array<string | number> | undefined],
  ];

  arrayParams.forEach(([key, values]) => {
    if (hasItems(values)) appendArray(key, values);
  });

  appendScalar("fetch_external", "true");

  return query;
};

export const toISOOrUndefined = (value: any): string | undefined => {
  if (!value) return undefined;
  try {
    if (typeof value?.toISO === "function") {
      const iso = value.toISO();
      return typeof iso === "string" && iso.length > 0 ? iso : undefined;
    }
    if (value instanceof Date) return value.toISOString();
    if (typeof value === "string") {
      const looksISO = /\d{4}-\d{2}-\d{2}T/.test(value);
      return looksISO ? value : new Date(value).toISOString();
    }
  } catch {
    return undefined;
  }
  return undefined;
};

const toArrayOrUndefined = (
  value?: string | number | Array<string | number> | null,
): Array<string | number> | undefined => {
  if (value === undefined || value === null) return undefined;
  if (Array.isArray(value)) return value.length ? value : undefined;
  const str = String(value);
  return str ? [str] : undefined;
};

const toNumberOrUndefined = (
  value?: string | number | null,
): number | undefined => {
  if (value === undefined || value === null || value === "") return undefined;
  if (typeof value === "number") return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const mapLegacyOrdersFiltersToV2 = (
  filters: IOrdersFilters,
  options?: { page?: number; limit?: number },
): IOrdersFiltersV2 => {
  const limit = options?.limit;
  const page = options?.page ?? 0;
  const skip = typeof limit === "number" ? page * limit : undefined;

  const date_from = toISOOrUndefined(filters.fromDate ?? filters["fromDate"]);
  const date_to = toISOOrUndefined(filters.toDate ?? filters["toDate"]);

  const status = toArrayOrUndefined(filters.status);
  const tracking_id = toArrayOrUndefined(filters.tracking_id);
  const carrier = toArrayOrUndefined(filters.carrier);
  const sla_alert_level = toArrayOrUndefined(filters.sla_alert_level);

  const merchantId = toNumberOrUndefined(
    filters.merchantId as unknown as string | number | null,
  );

  const searchRaw = filters["search"];
  const search =
    typeof searchRaw === "string" && searchRaw.length > 0
      ? searchRaw
      : undefined;
  const sortRaw = filters["sort"];
  const sort =
    typeof sortRaw === "string" && sortRaw.length > 0
      ? (sortRaw as IOrdersFiltersV2["sort"])
      : undefined;

  const v2Filters: IOrdersFiltersV2 = {
    skip,
    limit,
    date_from,
    date_to,
    status: status as string[] | undefined,
    tracking_id: tracking_id as string[] | undefined,
    carrier: carrier as string[] | undefined,
    merchantId,
    search,
    sort,
    sla_alert_level: sla_alert_level as string[] | undefined,
  };

  const v2FiltersWithoutUndefined = Object.fromEntries(
    Object.entries(v2Filters).filter(([, val]) => val !== undefined),
  ) as IOrdersFiltersV2;
  return v2FiltersWithoutUndefined;
};

export const getMappedInfo = (orders: IOrderRawWithMerchantName[]) =>
  orders.map(
    ({
      _id,
      merchant,
      merchant_name,
      createdAt,
      tracking_id,
      carrier: carrierFromOrder,
      service,
      status,
      status_history,
      sla_alert_level,
      orderInfo: {
        trackingNumber,
        carrier: carrierFromInfo,
        origin,
        destination,
        packages,
        labels,
        fee,
      },
      updatedAt,
      orderInfo,
      alias,
      shipment_units,
    }) => ({
      _id,
      createdAt,
      tracking_id:
        typeof tracking_id === "string" ? tracking_id : String(tracking_id),
      merchant,
      merchant_name,
      carrier: carrierFromInfo || carrierFromOrder,
      origin,
      destination,
      packages,
      labels,
      status,
      status_history,
      sla_alert_level,
      fee,
      service,
      trackingNumber,
      orderInfo,
      updatedAt,
      alias,
      shipment_units,
    }),
  );

export const getArrayOfNumbers = ({ length }: { length: number }) =>
  Array.from({ length }, (_, i) => i + 1);

export const checkIfPackageIsSelectedById = (
  packageId: string,
  packages: IPackageToRefund[],
) => !!packages.find(({ sku }) => sku === packageId);

// NOTE: this function would be useful when the retunning process be able to refund a partial order.
// export const checkIfProductIsSelectedById = (
//   id: string,
//   packages: IPackageToRefund[]
// ) => !!packages.find(({ sku: productId }) => productId === id);

export const findPackage = (packageId: string, packages: IPackageToRefund[]) =>
  packages.filter(({ sku }) => sku === packageId)[0];

export const removePackageToRefund = (
  _id: string,
  packages: IPackageToRefund[],
  setPackagesToRefund: React.Dispatch<React.SetStateAction<IPackageToRefund[]>>,
) => {
  const filteredPackages = packages.filter(({ sku }) => sku !== _id);
  setPackagesToRefund(filteredPackages);
};

export const compareDates = (
  date: string | Date | null | undefined,
  dateToCompare: string | Date | null | undefined,
) => {
  if (!date || !dateToCompare) return true;

  return new Date(dateToCompare) >= new Date(date);
};

export const capitalizeOnlyFirstLetter = (string: string) =>
  string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();

export const getFallenServicesMessage = (fallenServices: string[]) => {
  const uncapitalizedFallenServices = fallenServices.map((service) =>
    capitalizeOnlyFirstLetter(service),
  );
  const multipleServices = fallenServices.length > 1;
  const lastService = multipleServices && uncapitalizedFallenServices.pop();
  return `${multipleServices ? "Los servicios" : "El servicio"} de ${
    multipleServices
      ? `${uncapitalizedFallenServices.join(", ")} y ${lastService} presentan`
      : `${uncapitalizedFallenServices[0]} presenta`
  } interrupciones en el sistema. Las órdenes serán actualizadas a la brevedad.`;
};

export const formatSlaThreshold = (
  amount: number,
  unit: "business_hours" | "business_days"
): string => {
  if (amount === 0) return "";
  const unitLabel = unit === "business_hours" ? "hora" : "día";
  const pluralSuffix = amount > 1 ? "s" : "";
  return ` (${amount} ${unitLabel}${pluralSuffix})`;
};

export const canRefundOrCancel = (
  entity: TEntity,
  entitiesAbleToReturnOrCancel: TEntitiesAbleToExecuteAction,
) =>
  entity === ECOMMERCE ||
  ABLE_TO_REFUND_OR_CANCEL[entity].includes(entitiesAbleToReturnOrCancel);

export const getChoppedText = (totalCharacters: number, text: string) => {
  if (text.length > totalCharacters)
    return `${text.substring(0, totalCharacters)}...`;
  return text;
};

export const translateOrderStatus = (status?: string): string => {
  if (!status) return "";

  return STATUS_TRANSLATOR[status] || "";
};

const getRetriesText = (retries?: number) => {
  switch (retries) {
  case 1:
    return " en segunda visita";
  case 2:
    return " en tercera visita";
  default:
    return " en primera visita";
  }
};

export const extendStatusIfDelivered = (label?: string, retries?: number) =>
  label === SP_DELIVERED ? label.concat(getRetriesText(retries)) : label;

export const getUploadedCSVStatus = (
  csv: IUpdateOrderCsvResponseDTO,
): TCSVUploadStatus => {
  if (!csv) return "no-file";
  if (csv.errors_aws_key || csv.download_errors_csv_url) return "errors";
  if (csv.processed) return "processed";
  return "in-process";
};

export type PackageType = "optimized" | "mixed";
export const getPackageType = (packages: IShipmentUnit[]): PackageType => {
  const hasOptimized = packages.some((pkg) => pkg.used_virtual_box_id);
  const hasCatalog = packages.some((pkg) => !pkg.used_virtual_box_id);
  if (hasOptimized && hasCatalog) return "mixed";
  return "optimized";
};
